// Floor (spec/13 §3, F-STN-14 … F-STN-18): the venue console's home — the live picture of the room,
// not a wall of widgets. A slim strip of figures (occupancy, takings today, open food orders, overdue),
// a search and a filter with counts, then every station as a tile grouped by class: a live session owns its
// tile in the brand colour with its time left on a ring and the player's name under it; ending soon and
// overdue carry warn and danger chips; offline is dimmed with an icon and words. "What needs you" (F-STN-15)
// sits beside the grid, and a tile opens the station modal (F-STN-16) over the floor, never away from it, and
// its quick-actions menu (right-click, or the "⋯" on hover or focus) runs the common live-ops without opening it.
//
// The Floor is also where sessions are run (2026-10-07: the Sessions screen folded in here): the List view is the
// table of every station with who plays on it, the time left, until when and what it has cost so far; the search
// finds a station by number or name, or the player on it; and Offline play is one link away.
//
// The floor is kept current by the live push (`realtime.ts`) with the interval as the fallback; the
// clocks count down on the client against when each session was read (`live.ts`).

import type { BootFloorPc, NeedsAttention, Shift, StationCall, StationClass, StationCommandIssue, Zone } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Badge, Button, cx, EmptyState, SegmentedControl, Skeleton, StatCard, StatusPill, Table, type TableColumn, useToast } from '@clavian/ui-web';
import { useMemo, useRef, useState, type ReactNode } from 'react';

import { useAccess, type Access } from '../api/queries';
import { ConfirmDialog } from '../boot/ConfirmDialog';
import { hasUpdateWaiting, useFloorBoot, type BootTileState } from '../boot/floor-boot';
import { NewPcsTray } from '../boot/NewPcs';
import { useRestartIdleBootPcs } from '../boot/queries';
import { type BootProblemCard } from './boot-problems';
import { BootProblemsSection, coveredByCards, useBootProblems } from './BootProblems';
import { PROBLEM, problemCode } from '../api/problems';
import { useGuestPasses } from '../org/guests/queries';
import { useKitchenTickets } from '../org/kitchen/queries';
import { formatMoney } from '../org/sessions/money';
import { useOfflineSessionsToSettle } from '../org/sessions/offline-queries';
import { useSessionsLive } from '../org/sessions/realtime';
import { LoadFailure } from '../shell/LoadFailure';
import { Icon, NavIcon } from '../shell/nav-icons';
import { useCurrentVenue } from '../shell/scope';
import { useOptionalSurface } from '../surface';
import { usePrefersReducedMotion, useRealtimeConnection, useRealtimeStatus, useRealtimeUnavailable } from '../realtime';
import { useStationClasses, useZones } from '../stations/queries';
import { Forbidden, Notice, Page, SearchField, SummaryMeter, SummaryStrip, Toolbar, ToolbarSpring } from '../ui';
import { TickingNumber } from '../ui/ticking';
import { formatAgo } from './ago';
import { StockNeedsSection, useStockNeeds } from '../org/stock/StockNeedsYou';
import { FinishSetupBanner } from '../setup/FinishSetupBanner';
import { KioskTopUps, useKioskTopUpCount } from './KioskTopUps';
import { clockOf, playerNameOf, timeOfDay, useFloorPlayerNames, useFloorSessions, useNow, viewOf, type FloorState, type FloorStationView } from './live';
import { NowPlaying, stationNowPlaying } from './now-playing';
import { useAcknowledgeCall, useCalls, useFloor, useFloorFigures, useNeedsAttention, useResolveCall, useShiftVariances, useStationPower } from './queries';
import { FloorRoomView } from './FloorRoomView';
import { useCallsLive, useFloorFiguresLive, useFloorLive } from './realtime';
import { StationModal } from './StationModal';
import { TileWithQuickActions } from './quick-actions';
import { alertWords, FloorTile, pillOf } from './tile';

import './floor.css';
import { useProblem } from '../api/use-problem';

const FLOOR_READ = 'floor:read';
const SESSIONS_READ = 'sessions:read';
const PLAYERS_READ = 'players:read';
const WALLET_TOPUP = 'wallet:topup';
const CALLS_READ = 'calls:read';
const CALLS_MANAGE = 'calls:manage';
const KITCHEN_READ = 'kitchen:read';
const DAY_CLOSE = 'shifts:day_close';
const FLOOR_POWER = 'stations:power';
const SHIFTS_READ_ALL = 'shifts:read_all';
const FLOOR_ARRANGE = 'floor:arrange';
const PASSES_READ = 'passes:read';
const INVENTORY_READ = 'inventory:read';

/** Tiles by class, the room as arranged (F-STN-19), or a table. */
type ViewMode = 'grid' | 'room' | 'list';

/**
 * `update`: a network-started PC with an update waiting (Clavian Boot) — shown only where the venue has such PCs. `ready`,
 * `cant` and `bootOffline` are the Floor of PCs's own filters (a venue with Clavian Boot and no Clavian Stations): a PC
 * that is ready, one that cannot start, and one that cannot because the boot server is offline.
 */
type Filter = 'all' | 'free' | 'session' | 'ending' | 'overdue' | 'offline' | 'update' | 'ready' | 'cant' | 'bootOffline';
type Density = 'comfortable' | 'compact';

const FILTER_STATES: Readonly<Record<'free' | 'session' | 'ending' | 'overdue' | 'offline', ReadonlySet<FloorState>>> = {
  free: new Set(['free']),
  session: new Set(['session', 'ending', 'overdue', 'locked']),
  ending: new Set(['ending']),
  overdue: new Set(['overdue']),
  offline: new Set(['offline', 'disabled']),
};

/**
 * Whether a station answers the floor's search (spec/13 §3): its number ("7", or "#7"), a part of its name, or a part of
 * the name of the player on it. `needle` is already trimmed and lower-cased.
 */
export function matchesSearch(view: Pick<FloorStationView, 'station' | 'playerName'>, needle: string): boolean {
  const number = needle.replace(/^#/, '');
  return (
    (number !== '' && String(view.station.number).includes(number)) ||
    (view.station.name ?? '').toLowerCase().includes(needle) ||
    (view.playerName ?? '').toLowerCase().includes(needle)
  );
}

const DENSITY_KEY = 'clavian.floor.density';

function readDensity(): Density {
  try {
    return window.localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

function writeDensity(density: Density): void {
  try {
    window.localStorage.setItem(DENSITY_KEY, density);
  } catch {
    // A private window: the choice lasts until the page closes.
  }
}

export function FloorPage(): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const access = useAccess({ venueId: venue.id });
  const floor = useFloor(venue.organizationId, venue.id);
  // Consume the live `floor.station.updated` push while the socket is live; the interval is the fallback.
  useFloorLive(venue.organizationId, venue.id);
  useSessionsLive(venue.organizationId, venue.id);
  const classes = useStationClasses(venue.organizationId, venue.id);
  const zones = useZones(venue.organizationId, venue.id);
  // Clavian Boot (ADR-0065): how each network-started PC starts, on its tile and in its window. Empty without the add-on.
  // Every member who sees the Floor gets the tiles' simple state; the window's facts and actions need `boot:view`.
  const floorBoot = useFloorBoot({ organizationId: venue.organizationId, venueId: venue.id }, access);
  const boot = floorBoot.tiles;
  const bootFull = floorBoot.byStation;
  const ready = access.status === 'ready';
  // A venue with Clavian Boot and no Clavian Stations (ADR-0065): the Floor of PCs — no sessions, players, calls or selling.
  // Decided from the organisation's products, never assumed: a venue can add Clavian Stations later and this turns off.
  const bootOnly = !access.products.stations;
  const canSessions = ready && access.can(SESSIONS_READ) && !bootOnly;
  // Guests (F-SES-07): the venue's guest passes, linked from here where the venue serves guests.
  const guestList = useGuestPasses(venue.organizationId, venue.id, { enabled: ready && access.can(PASSES_READ) && !bootOnly });
  const showGuests = guestList.data?.guestsEnabled === true;
  // Calls for help, live over `venue:{v}:calls` (F-SES-25): the tiles flag a station with an open call.
  const canCalls = !bootOnly && (access.status !== 'ready' || access.can(CALLS_READ));
  useCallsLive(venue.organizationId, venue.id, canCalls);
  const callList = useCalls(venue.organizationId, venue.id, canCalls);
  const calledStations = useMemo(
    () => new Set(canCalls ? (callList.data?.calls ?? []).filter((call) => OPEN_CALL_STATES.has(call.state)).map((call) => call.stationId) : []),
    [canCalls, callList.data],
  );

  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [view, setView] = useState<ViewMode>('grid');
  const [density, setDensityState] = useState<Density>(readDensity);
  const [openStationId, setOpenStationIdState] = useState<string | null>(null);
  // The station the modal ended on, so closing it returns focus to that station's tile.
  const lastOpened = useRef<string | null>(null);
  const setOpenStationId = (stationId: string | null) => {
    if (stationId !== null) lastOpened.current = stationId;
    setOpenStationIdState(stationId);
  };
  const setDensity = (next: Density) => {
    setDensityState(next);
    writeDensity(next);
  };

  const stations = useMemo(() => floor.data?.stations ?? [], [floor.data]);
  const sessions = useFloorSessions(venue.organizationId, venue.id, stations, canSessions);
  // Who is playing (spec/13 §3.1): one profile read per player in session, with `players:read`.
  const names = useFloorPlayerNames(venue.organizationId, sessions, canSessions && access.can(PLAYERS_READ));
  const now = useNow();
  const walkIn = t('consoleSession.walkIn');
  const views = stations.map((station) => {
    const entry = sessions.get(station.stationId);
    const drawn = viewOf(station, entry, now);
    return { ...drawn, playerName: playerNameOf(drawn.session, names, walkIn, (code) => t('consoleSession.guest', { code })), boot: boot.get(station.stationId) };
  });

  const classById = useMemo(() => new Map((classes.data ?? []).map((klass) => [klass.id, klass])), [classes.data]);
  const zoneById = useMemo(() => new Map((zones.data ?? []).map((zone) => [zone.id, zone])), [zones.data]);
  // Problems with starting PCs from the boot server, as sentences with a fix (F-BOOT-30): for both products and Boot only.
  const bootCards = useBootProblems({ scope: { organizationId: venue.organizationId, venueId: venue.id }, access, views, bootFull, classById });

  if (floor.data === undefined) {
    if (floor.isError && problemCode(floor.error) === PROBLEM.forbidden) {
      return (
        <Page title={t('floor.title')}>
          <Forbidden message={t('floor.notPermitted')} />
        </Page>
      );
    }
    return floor.isError ? (
      <LoadFailure error={floor.error} message={t('floor.unavailable')} onRetry={() => void floor.refetch()} />
    ) : (
      <FloorSkeleton />
    );
  }

  const canView = access.status !== 'ready' || access.can(FLOOR_READ);
  // The search (spec/13 §3): a station's number or name, or the player on it. The filter's counts are of what it found.
  const needle = query.trim().toLowerCase();
  const searched = needle === '' ? views : views.filter((item) => matchesSearch(item, needle));
  const inFilter = (key: Exclude<Filter, 'all'>, item: FloorStationView): boolean => {
    // A PC out of order has one state, Out of order (spec/13 §3.8's precedence): it is in none of the boot filters.
    const pc = item.state === 'disabled' ? undefined : boot.get(item.station.stationId);
    switch (key) {
      case 'update':
        return pc !== undefined && hasUpdateWaiting(pc);
      case 'ready':
        return pc?.state === 'ready';
      case 'cant':
        return pc?.state === 'cant_start';
      case 'bootOffline':
        // The boot server is offline, or has gone quiet so a PC's state is unknown: both are the boot server's.
        return pc?.state === 'unknown' || (pc?.state === 'cant_start' && pc.problem === 'boot_server_offline');
      default:
        return FILTER_STATES[key].has(item.state);
    }
  };
  const count = (key: Exclude<Filter, 'all'>) => searched.filter((item) => inFilter(key, item)).length;
  // The Update waiting filter is offered only where the venue has network-started PCs (or it is the one chosen).
  const offersUpdate = boot.size > 0 || filter === 'update';
  const bootStates = (state: BootTileState['state']) => views.filter((item) => item.state !== 'disabled' && boot.get(item.station.stationId)?.state === state);
  const shown = filter === 'all' ? searched : searched.filter((item) => inFilter(filter, item));
  const openView = views.find((item) => item.station.stationId === openStationId) ?? null;
  // Previous and Next in the modal walk the stations in view by number, wrapping; the whole floor when the open
  // one is filtered out.
  const stepOrder = [...(shown.some((item) => item.station.stationId === openStationId) ? shown : views)].sort((a, b) => a.station.number - b.station.number);
  const step = (direction: -1 | 1) => {
    const at = stepOrder.findIndex((item) => item.station.stationId === openStationId);
    const next = at < 0 ? undefined : stepOrder[(at + direction + stepOrder.length) % stepOrder.length];
    if (next !== undefined) setOpenStationId(next.station.stationId);
  };
  const returnFocus = (event: Event) => {
    const id = lastOpened.current;
    const tile = id === null ? null : document.querySelector<HTMLElement>(`[data-station-tile="${id}"]`);
    if (tile === null) return;
    event.preventDefault();
    tile.focus();
  };
  const liveCount = views.filter((item) => FILTER_STATES.session.has(item.state)).length;

  // The page's own links sit in its header, beside the title (2026-10-07 uplift): Offline play — staff codes to
  // print before an outage, and what PCs ran offline to settle (spec/13 §3.6) — and Arrange. The toolbar under it
  // is only what shapes the floor in view: the search and how it is drawn, then the filter right over the tiles.
  const pageLinks =
    canSessions || showGuests || (ready && access.can(FLOOR_ARRANGE)) ? (
      <>
        {showGuests && !bootOnly ? (
          <Button asChild variant="secondary" size="sm">
            <Link to="/v/$venueSlug/sessions/guests" params={{ venueSlug: venue.slug }}>
              {t('guests.link')}
            </Link>
          </Button>
        ) : null}
        {canSessions ? (
          <Button asChild variant="secondary" size="sm">
            <Link to="/v/$venueSlug/sessions/offline" params={{ venueSlug: venue.slug }}>
              {t('sessions.offline.link')}
            </Link>
          </Button>
        ) : null}
        {ready && access.can(FLOOR_ARRANGE) ? (
          <Button asChild variant="secondary" size="sm">
            <Link to="/v/$venueSlug/floor/arrange" params={{ venueSlug: venue.slug }}>
              {t('floor.arrange.open')}
            </Link>
          </Button>
        ) : null}
      </>
    ) : undefined;

  return (
    <Page
      width="wide"
      title={t('floor.title')}
      description={bootOnly ? t('boot.bootOnly.intro', { count: stations.length }) : t('floor.intro', { n: stations.length, live: liveCount })}
      meta={<FloorLiveStatus />}
      actions={pageLinks}
    >
      {/* A venue setting up with no pass on sale: say so first, with the way to make one (ADR-0058). */}
      {bootOnly ? null : <FinishSetupBanner />}
      {bootOnly ? <BootFigures views={views} boot={boot} /> : <FloorFigures views={views} access={access} />}

      <div className="console-floor__layout">
        <div className="console-floor__main">
          {/* PCs that started from the boot server and are not stations yet (ADR-0065): identify one, make it a station.
              Dragging a card onto the floor plan (Room view, Arrange) is not built yet: a station made here is unplaced. */}
          <NewPcsTray scope={{ organizationId: venue.organizationId, venueId: venue.id }} access={access} stations={stations} />
          <div className="console-floor__controls">
            <Toolbar>
              <SearchField
                className="console-floor__search"
                label={bootOnly ? t('boot.bootOnly.search.label') : t('floor.search.label')}
                placeholder={bootOnly ? t('boot.bootOnly.search.placeholder') : t('floor.search.placeholder')}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <ToolbarSpring />
              {view === 'grid' ? (
                <SegmentedControl<Density>
                  size="sm"
                  label={t('floor.density.label')}
                  value={density}
                  onValueChange={setDensity}
                  options={[
                    { value: 'comfortable', label: t('floor.density.comfortable') },
                    { value: 'compact', label: t('floor.density.compact') },
                  ]}
                />
              ) : null}
              <SegmentedControl<ViewMode>
                size="sm"
                label={t('common.view.label')}
                value={view}
                onValueChange={setView}
                options={[
                  { value: 'grid', label: t('common.view.grid'), icon: <Icon name="grid" /> },
                  { value: 'room', label: t('floor.room.view'), icon: <NavIcon name="floor" /> },
                  { value: 'list', label: t('common.view.list'), icon: <Icon name="list" /> },
                ]}
              />
            </Toolbar>
            <SegmentedControl<Filter>
              label={t('floor.filter.label')}
              value={filter}
              onValueChange={setFilter}
              className="console-floor__filter"
              options={
                bootOnly
                  ? [
                      { value: 'all', label: t('floor.filter.all'), count: searched.length },
                      { value: 'ready', label: t('boot.bootOnly.filter.ready'), count: count('ready') },
                      { value: 'update', label: t('floor.filter.update'), count: count('update') },
                      { value: 'cant', label: t('boot.bootOnly.filter.cantStart'), count: count('cant') },
                      { value: 'bootOffline', label: t('boot.bootOnly.filter.offline'), count: count('bootOffline') },
                    ]
                  : [
                      { value: 'all', label: t('floor.filter.all'), count: searched.length },
                      { value: 'free', label: t('floor.filter.free'), count: count('free') },
                      { value: 'session', label: t('floor.filter.session'), count: count('session') },
                      { value: 'ending', label: t('floor.filter.ending'), count: count('ending') },
                      { value: 'overdue', label: t('floor.filter.overdue'), count: count('overdue') },
                      { value: 'offline', label: t('floor.filter.offline'), count: count('offline') },
                      ...(offersUpdate ? [{ value: 'update' as const, label: t('floor.filter.update'), count: count('update') }] : []),
                    ]
              }
            />
          </div>

          {filter === 'update' && shown.length > 0 ? (
            <UpdatesWaiting count={shown.length} pcs={shown.flatMap((item) => bootFull.get(item.station.stationId) ?? [])} access={access} />
          ) : null}

          {stations.length === 0 && bootOnly ? (
            <EmptyState icon={<NavIcon name="stations" />} title={t('boot.bootOnly.emptyTitle')} description={t('boot.bootOnly.empty')} />
          ) : stations.length === 0 ? (
            <EmptyState icon={<NavIcon name="stations" />} title={t('floor.emptyTitle')} description={t('floor.empty')} action={<Link className="clav-button clav-button--primary" to="/v/$venueSlug/stations" params={{ venueSlug: venue.slug }}>{t('floor.emptyAction')}</Link>} />
          ) : searched.length === 0 ? (
            <EmptyState
              size="compact"
              icon={<Icon name="search" />}
              title={bootOnly ? t('boot.bootOnly.search.empty', { query: query.trim() }) : t('floor.search.empty', { query: query.trim() })}
              action={
                <Button variant="secondary" size="sm" onClick={() => setQuery('')}>
                  {t('common.clearSearch')}
                </Button>
              }
            />
          ) : shown.length === 0 ? (
            <EmptyState
              size="compact"
              icon={<Icon name="check" />}
              title={bootOnly && (filter === 'ready' || filter === 'cant' || filter === 'bootOffline') ? t(`boot.bootOnly.filterEmpty.${filter === 'bootOffline' ? 'offline' : filter === 'cant' ? 'cantStart' : 'ready'}`) : t(`floor.filterEmpty.${filter}`)}
              action={
                <Button variant="secondary" size="sm" onClick={() => setFilter('all')}>
                  {t('floor.filter.showAll')}
                </Button>
              }
            />
          ) : view === 'grid' ? (
            <PlanView views={shown} classById={classById} density={density} called={calledStations} boot={boot} access={access} bootOnly={bootOnly} onOpen={setOpenStationId} />
          ) : view === 'room' ? (
            <FloorRoomView organizationId={venue.organizationId} venueId={venue.id} views={shown} zones={zones.data ?? []} called={calledStations} onOpen={setOpenStationId} />
          ) : (
            <ListView views={shown} classById={classById} zoneById={zoneById} canSessions={canSessions} bootOnly={bootOnly} onOpen={setOpenStationId} />
          )}
        </div>
        {bootOnly ? (
          <BootNeedsYou
            cantStart={bootFull.size > 0 ? [] : bootStates('cant_start')}
            unknown={bootStates('unknown')}
            updateWaiting={bootStates('update_waiting').filter((item) => !coveredByCards(bootCards).has(item.station.stationId))}
            cards={bootCards}
            access={access}
            venue={venue}
            bootFull={bootFull}
            onOpen={setOpenStationId}
          />
        ) : (
          <WhatNeedsYou views={views} onOpen={setOpenStationId} access={access} venue={venue} classById={classById} bootCards={bootCards} bootFull={bootFull} />
        )}
      </div>

      {canView ? (
        <StationModal
          view={openView}
          klass={openView === null ? undefined : classById.get(openView.station.classId)}
          zone={openView === null || openView.station.zoneId === null ? undefined : zoneById.get(openView.station.zoneId)}
          access={access}
          boot={openView === null ? undefined : bootFull.get(openView.station.stationId)}
          onClose={() => setOpenStationId(null)}
          onStep={stepOrder.length > 1 ? step : undefined}
          onCloseAutoFocus={returnFocus}
        />
      ) : null}
    </Page>
  );
}

// ---------------------------------------------------------------------------------------------
// The live-connection chip (spec/09 §4, §8): the floor says plainly whether it is live. Ember is
// kept for actions and a session, so this is a status in words with a dot — green and breathing while
// the socket carries the push, amber while it reconnects, and quiet "Paused" when live updates are off
// (reduced motion, where the floor refreshes on a timer). The owner must never mistake a polled floor
// for a live one — that is exactly when an outage makes the picture lag.
//
// "Connecting" is honest for a blip, not for an outage: past `REALTIME_UNAVAILABLE_AFTER_MS` without a live
// socket the chip says live updates are paused and offers a retry (and the hint, a reload). Meanwhile the
// floor's reads poll (`queries.ts`: every read polls whenever the socket is not live), so the picture stays
// current to within the interval either way.
// ---------------------------------------------------------------------------------------------

export function FloorLiveStatus(): ReactNode {
  const { t } = useTranslation('console');
  const status = useRealtimeStatus();
  const connection = useRealtimeConnection();
  const unavailable = useRealtimeUnavailable();
  const reducedMotion = usePrefersReducedMotion();
  // The public demo opens no socket: its venue is in the page, and a change from the kiosk tab reaches the floor at
  // once (spec/24 §2.3) — live, not reconnecting.
  const inPage = useOptionalSurface()?.realtime === false;
  // `offline` covers backing-off-and-retrying, an outage, and the reduced-motion case where the socket is never
  // opened. The first is honestly "connecting"; the second, once it has lasted, is "unavailable" with a retry; the
  // third is a deliberate "paused".
  const state: 'live' | 'connecting' | 'unavailable' | 'paused' =
    status === 'live' || inPage ? 'live' : reducedMotion ? 'paused' : unavailable ? 'unavailable' : 'connecting';
  const words = {
    live: t('floor.live'),
    connecting: t('floor.liveConnecting'),
    unavailable: t('floor.liveUnavailable'),
    paused: t('floor.livePaused'),
  }[state];
  const hint = {
    live: t('floor.liveHint'),
    connecting: t('floor.liveConnectingHint'),
    unavailable: t('floor.liveUnavailableHint'),
    paused: t('floor.livePausedHint'),
  }[state];
  const chip = (
    <span className={cx('console-floor__live', `console-floor__live--${state}`)} title={hint} role="status" aria-label={t('floor.liveStatus', { state: words })}>
      <span className={cx('console-floor__live-dot', state === 'live' && 'console-floor__live-dot--beat')} aria-hidden="true" />
      {words}
    </span>
  );
  if (state !== 'unavailable' || connection === null) return chip;
  return (
    <span className="console-floor__live-group">
      {chip}
      <Button variant="secondary" size="sm" onClick={() => connection.retry()} title={hint}>
        {t('floor.liveRetry')}
      </Button>
    </span>
  );
}

// The floor while it first loads (BRAND.md §6.1: a Skeleton in the final layout, never a blank): a
// header-shaped block, the figures strip, then a grid of tile-shaped skeletons in the floor's own
// layout — so the swap to the live room moves nothing. The heading is a skeleton, not a real one, so
// the screen reader is told the floor is loading (the status line) rather than reading an empty title.
function FloorSkeleton(): ReactNode {
  const { t } = useTranslation('console');
  return (
    <div className="console-page console-page--wide" role="status" aria-busy="true" aria-label={t('app.loading')}>
      <div aria-hidden="true" className="console-floor__skeleton-head">
        <Skeleton shape="text" width="7rem" height="2rem" />
        <Skeleton shape="text" width="16rem" />
      </div>
      <div className="console-summary" aria-hidden="true">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} height="5.75rem" />
        ))}
      </div>
      <div className="console-floor__layout" aria-hidden="true">
        <div className="console-floor__main">
          <Skeleton shape="text" width="9rem" height="1.25rem" />
          <div className="console-floor__tiles">
            {Array.from({ length: 10 }, (_, index) => (
              <Skeleton key={index} height="7.5rem" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The figures: a slim strip of StatCards
// ---------------------------------------------------------------------------------------------

function FloorFigures({ views, access }: { readonly views: readonly FloorStationView[]; readonly access: Access }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const venue = useCurrentVenue();
  const ready = access.status === 'ready';
  const canKitchen = ready && access.can(KITCHEN_READ);
  const canTakings = ready && access.can(DAY_CLOSE);
  const tickets = useKitchenTickets(venue.organizationId, venue.id, undefined, canKitchen);
  // F-ANL-02: takings today is M-01 for the business day so far, from the server — the figure the day's report shows,
  // never the tenders added up here (a cash wallet top-up is in the drawer but is not revenue).
  const figures = useFloorFigures(venue.organizationId, venue.id, canTakings);
  useFloorFiguresLive(venue.organizationId, venue.id, canTakings);

  const total = views.filter((item) => item.state !== 'disabled').length;
  const live = views.filter((item) => FILTER_STATES.session.has(item.state)).length;
  const overdue = views.filter((item) => item.state === 'overdue').length;
  const ending = views.filter((item) => item.state === 'ending').length;
  const free = views.filter((item) => item.state === 'free').length;
  const percent = total === 0 ? 0 : Math.round((live / total) * 100);
  const takings = figures.data?.takings ?? undefined;

  return (
    <SummaryStrip label={t('floor.figures.label')}>
      {/* The live figure leads the strip (ADR-0043): its ember rail matches the session tiles, and a meter under it
          shows how full the room is. The figures tick to a new value rather than jumping. */}
      <StatCard
        className="console-summary__cell--live"
        label={t('floor.figures.occupancy')}
        value={<TickingNumber className="console-num" value={live} format={String} />}
        unit={t('floor.figures.ofTotal', { total })}
        hint={
          <>
            <SummaryMeter fill={total === 0 ? 0 : live / total} />
            {t('floor.figures.inUse', { percent })}
          </>
        }
      />
      {canTakings ? (
        <StatCard
          label={t('floor.figures.takings')}
          value={
            takings === undefined ? (
              <span className="console-num">—</span>
            ) : (
              <TickingNumber className="console-num" value={takings.amount} format={(amount) => formatMoney({ ...takings, amount })} />
            )
          }
          hint={
            figures.isError
              ? problem.text(figures.error, { fallback: t('floor.figures.unreadable') })
              : figures.data === undefined
                ? t('app.loading')
                : takings === undefined
                  ? t('floor.figures.nothingTaken')
                  : t('floor.figures.businessDay', { day: figures.data.businessDay })
          }
        />
      ) : (
        <StatCard label={t('floor.figures.free')} value={<TickingNumber className="console-num" value={free} format={String} />} hint={t('floor.figures.freeHint')} />
      )}
      {canKitchen ? (
        <StatCard
          label={t('floor.figures.orders')}
          value={
            tickets.data === undefined ? (
              <span className="console-num">—</span>
            ) : (
              <TickingNumber className="console-num" value={tickets.data.length} format={String} />
            )
          }
          hint={tickets.isError ? t('floor.figures.unreadable') : t('floor.figures.ordersHint')}
        />
      ) : (
        <StatCard
          label={t('floor.figures.ending')}
          value={<TickingNumber className="console-num" value={ending} format={String} />}
          tone={ending > 0 ? 'warn' : 'default'}
          hint={t('floor.figures.endingHint')}
        />
      )}
      <StatCard
        label={t('floor.figures.overdue')}
        value={<TickingNumber className="console-num" value={overdue} format={String} />}
        tone={overdue > 0 ? 'danger' : 'default'}
        hint={canKitchen ? t('floor.figures.overdueHint', { count: ending }) : t('floor.figures.overdueAlone')}
      />
    </SummaryStrip>
  );
}

// ---------------------------------------------------------------------------------------------
// The Floor of PCs's figures and "What needs you": a venue with Clavian Boot and no Clavian Stations has no sessions to
// count, so the strip counts the PCs ready of the total, those with an update waiting and those that cannot start; and the
// panel beside the floor lists the boot problems (ADR-0065).
// ---------------------------------------------------------------------------------------------

function BootFigures({ views, boot }: { readonly views: readonly FloorStationView[]; readonly boot: ReadonlyMap<string, BootTileState> }): ReactNode {
  const { t } = useTranslation('console');
  // A PC out of order is not counted at all: it is neither ready nor waiting (spec/13 §3.8's precedence).
  const inUse = views.filter((item) => item.state !== 'disabled');
  const total = inUse.length;
  const states = inUse.map((item) => boot.get(item.station.stationId)?.state);
  const ready = states.filter((state) => state === 'ready').length;
  const waiting = states.filter((state) => state === 'update_waiting').length;
  const cant = states.filter((state) => state === 'cant_start').length;
  const percent = total === 0 ? 0 : Math.round((ready / total) * 100);
  return (
    <SummaryStrip label={t('boot.bootOnly.figures.label')}>
      <StatCard
        label={t('boot.bootOnly.figures.ready')}
        value={<TickingNumber className="console-num" value={ready} format={String} />}
        unit={t('boot.bootOnly.figures.ofTotal', { total })}
        hint={
          <>
            <SummaryMeter fill={total === 0 ? 0 : ready / total} />
            {t('boot.bootOnly.figures.readyHint', { percent })}
          </>
        }
      />
      <StatCard
        label={t('boot.bootOnly.figures.update')}
        value={<TickingNumber className="console-num" value={waiting} format={String} />}
        tone={waiting > 0 ? 'warn' : 'default'}
        hint={t('boot.bootOnly.figures.updateHint')}
      />
      <StatCard
        label={t('boot.bootOnly.figures.cantStart')}
        value={<TickingNumber className="console-num" value={cant} format={String} />}
        tone={cant > 0 ? 'danger' : 'default'}
        hint={t('boot.bootOnly.figures.cantStartHint')}
      />
    </SummaryStrip>
  );
}

function BootNeedsYou({
  cantStart,
  unknown,
  updateWaiting,
  cards,
  access,
  venue,
  bootFull,
  onOpen,
}: {
  /** PCs that cannot start, listed plainly for a member who cannot read the boot summary (it has no cards). */
  readonly cantStart: readonly FloorStationView[];
  /** PCs whose state the boot server stopped reporting. */
  readonly unknown: readonly FloorStationView[];
  readonly updateWaiting: readonly FloorStationView[];
  /** The problems with a fix, one card each (F-BOOT-30). */
  readonly cards: readonly BootProblemCard[];
  readonly access: Access;
  readonly venue: { readonly id: string; readonly organizationId: string; readonly slug: string };
  readonly bootFull: ReadonlyMap<string, BootFloorPc>;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const attention = cantStart.length + unknown.length + updateWaiting.length + cards.length;
  return (
    <aside className="console-floor__panel" aria-labelledby="console-floor-needs">
      <header className="console-floor__panel-head">
        <h2 id="console-floor-needs" className="console-floor__panel-title">
          {t('floor.needsYou.title')}
        </h2>
        {attention === 0 ? null : <Badge tone="neutral">{attention}</Badge>}
      </header>
      {attention === 0 ? (
        <div className="console-floor__clear">
          <span className="console-floor__clear-icon" aria-hidden="true">
            <Icon name="check" />
          </span>
          <p>{t('boot.bootOnly.needs.clear')}</p>
        </div>
      ) : null}
      <BootProblemsSection cards={cards} access={access} venueSlug={venue.slug} scope={{ organizationId: venue.organizationId, venueId: venue.id }} bootFull={bootFull} onOpen={onOpen} />
      <PanelSection title={t('boot.bootOnly.needs.cantStart')} items={cantStart} onOpen={onOpen} tone="danger" />
      <PanelSection title={t('boot.bootOnly.needs.unknown')} items={unknown} onOpen={onOpen} tone="neutral" />
      <PanelSection title={t('boot.bootOnly.needs.updateWaiting')} items={updateWaiting} onOpen={onOpen} tone="warn" />
    </aside>
  );
}

// ---------------------------------------------------------------------------------------------
// The grid — tiles grouped by class
// ---------------------------------------------------------------------------------------------

interface ClassGroup {
  readonly classId: string;
  readonly name: string;
  readonly colorToken: string | undefined;
  readonly views: readonly FloorStationView[];
}

function groupByClass(views: readonly FloorStationView[], classById: ReadonlyMap<string, StationClass>): readonly ClassGroup[] {
  const byClass = new Map<string, FloorStationView[]>();
  for (const item of views) {
    const list = byClass.get(item.station.classId) ?? [];
    list.push(item);
    byClass.set(item.station.classId, list);
  }
  return [...byClass.entries()]
    .map(([classId, list]) => {
      const klass = classById.get(classId);
      return {
        classId,
        name: klass?.name ?? classId,
        colorToken: klass?.colorToken,
        views: [...list].sort((a, b) => a.station.number - b.station.number),
      };
    })
    .sort((a, b) => (a.views[0]?.station.number ?? 0) - (b.views[0]?.station.number ?? 0));
}

function PlanView({
  views,
  classById,
  density,
  called,
  boot,
  access,
  bootOnly,
  onOpen,
}: {
  readonly views: readonly FloorStationView[];
  readonly classById: ReadonlyMap<string, StationClass>;
  /** The venue has no Clavian Stations: a group counts the PCs that are ready, not the ones in session. */
  readonly bootOnly: boolean;
  readonly density: Density;
  /** Stations with an open call for help (F-SES-25): their tiles carry the flag. */
  readonly called: ReadonlySet<string>;
  /** Network-started PCs (Clavian Boot), by station: their tiles carry the boot strip. */
  readonly boot: ReadonlyMap<string, BootTileState>;
  /** Which quick actions each tile's menu offers. */
  readonly access: Access;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const groups = useMemo(() => groupByClass(views, classById), [views, classById]);
  return (
    <div className={`console-floor__groups console-floor__groups--${density}`}>
      {groups.map((group) => {
        const free = group.views.filter((item) => item.state === 'free').length;
        const live = group.views.filter((item) => FILTER_STATES.session.has(item.state)).length;
        const ready = group.views.filter((item) => item.state !== 'disabled' && item.boot?.state === 'ready').length;
        return (
          <section key={group.classId} className="console-floor__group" aria-label={group.name}>
            <header className="console-floor__group-head">
              <span className="console-floor__class-dot" style={{ backgroundColor: group.colorToken === undefined ? undefined : `var(--clavian-${group.colorToken})` }} aria-hidden="true" />
              <h2 className="console-floor__group-title">{group.name}</h2>
              <span className="console-subtle console-num">
                {bootOnly ? t('boot.bootOnly.groupCount', { ready, total: group.views.length }) : t('floor.group.count', { live, free, total: group.views.length })}
              </span>
            </header>
            <div className="console-floor__tiles">
              {group.views.map((item) => (
                <TileWithQuickActions key={item.station.stationId} view={item} access={access} onOpen={onOpen}>
                  <FloorTile view={item} className={group.name} colorToken={group.colorToken} called={called.has(item.station.stationId)} boot={boot.get(item.station.stationId)} waitingForApp={access.products.stations} onOpen={onOpen} />
                </TileWithQuickActions>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Update waiting (Clavian Boot, ADR-0065): the PCs whose Windows has a newer version, and Restart all when free — the
// rollout's own restart for exactly these PCs, one request per image, never during a session or with a booking soon.
// ---------------------------------------------------------------------------------------------

function UpdatesWaiting({ count, pcs, access }: { readonly count: number; readonly pcs: readonly BootFloorPc[]; readonly access: Access }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const venue = useCurrentVenue();
  const restart = useRestartIdleBootPcs({ organizationId: venue.organizationId, venueId: venue.id });
  const [asking, setAsking] = useState(false);
  const canRestart = access.status === 'ready' && access.can('boot:manage');
  const byImage = new Map<string, string[]>();
  for (const pc of pcs) {
    if (pc.image === null || pc.editing) continue;
    byImage.set(pc.image.id, [...(byImage.get(pc.image.id) ?? []), pc.bootPcId]);
  }

  async function run(): Promise<void> {
    let restarted = 0;
    let skipped = 0;
    for (const [imageId, bootPcIds] of byImage) {
      const result = await restart.mutateAsync({ imageId, bootPcIds });
      restarted += result.restarted.length;
      skipped += result.skipped.length;
    }
    show({ tone: restarted > 0 ? 'ok' : 'neutral', title: t('boot.floor.updates.result', { restarted, skipped }) });
  }

  return (
    <Notice
      tone="warn"
      className="console-floor__updates"
      action={
        canRestart && byImage.size > 0 ? (
          <Button variant="secondary" size="sm" onClick={() => setAsking(true)}>
            {t('boot.floor.updates.restartAll')}
          </Button>
        ) : undefined
      }
    >
      {t('boot.floor.updates.summary', { count })}
      {asking ? (
        <ConfirmDialog
          open
          onClose={() => setAsking(false)}
          title={t('boot.floor.updates.title')}
          description={t('boot.floor.updates.body')}
          confirmLabel={t('boot.floor.updates.confirm')}
          cancelLabel={t('common.cancel')}
          failedText={t('boot.floor.updates.failed')}
          onConfirm={run}
        />
      ) : null}
    </Notice>
  );
}

// ---------------------------------------------------------------------------------------------
// The list (spec/13 §3.1): every station with who plays on it, what it runs, the time left, until when and what
// it has cost so far — the table the Sessions screen was, before it folded into the Floor (2026-10-07).
// ---------------------------------------------------------------------------------------------

/** A running session on a view, or null: a free station shows its state and nothing else. */
function runningOf(item: FloorStationView) {
  return item.session === null || item.session === undefined || item.session.status === 'ended' ? null : item.session;
}

function ListView({
  views,
  classById,
  zoneById,
  canSessions,
  bootOnly,
  onOpen,
}: {
  readonly views: readonly FloorStationView[];
  readonly classById: ReadonlyMap<string, StationClass>;
  readonly zoneById: ReadonlyMap<string, Zone>;
  /** The venue has no Clavian Stations: there is no clock to show. */
  readonly bootOnly: boolean;
  /** The role reads sessions: the player, until and charged columns have something to say. */
  readonly canSessions: boolean;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const columns: TableColumn<FloorStationView>[] = [
    {
      key: 'number',
      header: t('floor.column.station'),
      cell: (item) => {
        const klass = classById.get(item.station.classId)?.name;
        const zone = item.station.zoneId === null ? undefined : zoneById.get(item.station.zoneId)?.name;
        return (
          <button type="button" className="console-cell-link" data-station-tile={item.station.stationId} onClick={() => onOpen(item.station.stationId)} title={zone}>
            <span>{item.station.name ?? t('floor.tile.numbered', { number: item.station.number })}</span>
            <span className="console-cell-sub console-num">
              {[t('floor.column.numberShort', { number: item.station.number }), klass].filter((part) => part !== undefined).join(' · ')}
            </span>
          </button>
        );
      },
    },
    {
      key: 'status',
      header: t('floor.column.status'),
      cell: (item) => {
        const pill = pillOf(item, t);
        // The state in words; the clock is the time-left column's, as on the tile it is the ring's.
        const words = item.state === 'session' ? t('floor.state.session') : item.state === 'ending' ? t('floor.state.ending') : pill.words;
        // What it runs now (F-CAT-04): under the state, as on the tile.
        const playing = runningOf(item) === null ? null : stationNowPlaying(item.station, item.now, t);
        return (
          <span className="console-floor__list-state">
            <StatusPill state={pill.state} size="sm">
              {words}
            </StatusPill>
            <NowPlaying text={playing} />
          </span>
        );
      },
    },
  ];
  if (canSessions) {
    columns.push({
      key: 'player',
      header: t('floor.column.player'),
      cell: (item) => {
        const session = runningOf(item);
        if (session === null) return null;
        // The name where it is known; else the short id (a role without `players:read`), the whole id on hover.
        return item.playerName !== null ? (
          <span className="console-floor__list-player">{item.playerName}</span>
        ) : (
          <span className="console-code" title={session.playerId} aria-label={session.playerId}>
            {t('sessions.field.playerShort', { id: session.playerId.slice(-6).toUpperCase() })}
          </span>
        );
      },
    });
  }
  if (!bootOnly) columns.push({
    key: 'left',
    header: t('floor.column.timeLeft'),
    numeric: true,
    cell: (item) => {
      // The time left, and under it the time it plays until (the Sessions table's two columns, in one).
      const until = timeOfDay(runningOf(item)?.fundedEndAt ?? null);
      return item.state === 'overdue' ? (
        <span className="console-floor__list-overdue">{t('floor.state.overdue')}</span>
      ) : item.secondsLeft === null ? null : (
        <span className="console-floor__list-time">
          <span className="clav-figure">{clockOf(item.secondsLeft)}</span>
          {until === null ? null : <span className="console-cell-sub">{t('floor.list.until', { time: until })}</span>}
        </span>
      );
    },
  });
  if (canSessions) {
    columns.push({
      key: 'charged',
      header: t('floor.column.charged'),
      numeric: true,
      cell: (item) => {
        const session = runningOf(item);
        if (session === null) return null;
        // A booked console session (ADR-0034) is charged nothing as it runs: what is due at the end.
        return session.booking === undefined ? (
          <span className="console-money clav-figure">{formatMoney(session.charged.chargedAmount)}</span>
        ) : (
          <span className="console-floor__list-time">
            <span className="console-money clav-figure">{formatMoney(session.booking.quote.total)}</span>
            <span className="console-cell-sub">{t('floor.column.due')}</span>
          </span>
        );
      },
    });
  }
  return (
    <div className="console-table-card">
      <Table caption={t('floor.list.caption')} captionHidden columns={columns} rows={views} rowKey={(item) => item.station.stationId} empty={t('floor.empty')} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// "What needs you" (F-STN-15)
// ---------------------------------------------------------------------------------------------

function WhatNeedsYou({
  views,
  onOpen,
  access,
  venue,
  classById,
  bootCards,
  bootFull,
}: {
  readonly views: readonly FloorStationView[];
  readonly onOpen: (stationId: string) => void;
  readonly access: Access;
  readonly venue: { readonly id: string; readonly organizationId: string; readonly slug: string };
  readonly classById: ReadonlyMap<string, StationClass>;
  /** Problems with starting PCs from the boot server, one card each (F-BOOT-30). */
  readonly bootCards: readonly BootProblemCard[];
  readonly bootFull: ReadonlyMap<string, BootFloorPc>;
}): ReactNode {
  const { t } = useTranslation('console');
  const navigate = useNavigate();
  const byState = (state: FloorState) => views.filter((item) => item.state === state);
  const overdue = byState('overdue');
  const ending = [...byState('ending')].sort((a, b) => (a.secondsLeft ?? 0) - (b.secondsLeft ?? 0));
  const free = byState('free');
  const offline = byState('offline');
  const disabled = byState('disabled');
  const maintenance = byState('maintenance');
  const canReadCalls = access.status !== 'ready' || access.can(CALLS_READ);
  const canPower = access.status === 'ready' && access.can(FLOOR_POWER);
  const calls = useCalls(venue.organizationId, venue.id, canReadCalls);
  const openCalls = canReadCalls ? (calls.data?.calls ?? []).filter((call) => OPEN_CALL_STATES.has(call.state)) : [];
  // Section 2, "Needs attention" (spec/13 §3.2): open station alerts and failed or expired commands (F-STN-15).
  const needs = useNeedsAttention(venue.organizationId, venue.id, access.status !== 'ready' || access.can(FLOOR_READ));
  const needsCount = (needs.data?.alerts.length ?? 0) + (needs.data?.commands.length ?? 0);
  // F-OPS-07: shifts closed with a variance over the owner's alert threshold, until the day close reviews them.
  const canReadShifts = access.status === 'ready' && access.can(SHIFTS_READ_ALL);
  const variances = useShiftVariances(venue.organizationId, venue.id, canReadShifts);
  const varianceShifts = canReadShifts ? (variances.data?.shifts ?? []) : [];
  // F-KIO-10: top-ups a player asked for at a PC, waiting on the desk to take the money (`wallet:topup`).
  const canTopUp = access.status === 'ready' && access.can(WALLET_TOPUP);
  const topUps = useKioskTopUpCount(venue.organizationId, venue.id, canTopUp);
  // Offline play (spec/13 §3.6): sessions PCs ran offline that staff must still settle (`sessions:read`).
  const canSessions = access.status === 'ready' && access.can(SESSIONS_READ);
  const toSettle = useOfflineSessionsToSettle(venue.organizationId, venue.id, canSessions);
  const settleCount = canSessions ? (toSettle.data?.length ?? 0) : 0;
  // Stock (spec/13 §6.4): low, out and below-zero items and the dishes stock sold out, where stock is on (`inventory:read`).
  const stockNeeds = useStockNeeds(venue.organizationId, venue.id, access.status === 'ready' && access.can(INVENTORY_READ));
  // "Free now" is helpful context, not a task, so it is not part of the attention count or "all clear".
  const attention =
    overdue.length + ending.length + offline.length + disabled.length + maintenance.length + openCalls.length + needsCount + varianceShifts.length + topUps + settleCount + stockNeeds.count + bootCards.length;
  const nothing = attention === 0;

  return (
    <aside className="console-floor__panel" aria-labelledby="console-floor-needs">
      <header className="console-floor__panel-head">
        <h2 id="console-floor-needs" className="console-floor__panel-title">
          {t('floor.needsYou.title')}
        </h2>
        {nothing ? null : <Badge tone="neutral">{attention}</Badge>}
      </header>
      {nothing ? (
        <div className="console-floor__clear">
          <span className="console-floor__clear-icon" aria-hidden="true">
            <Icon name="check" />
          </span>
          <p>{t('floor.needsYou.clear')}</p>
        </div>
      ) : null}

      {canReadCalls ? (
        <CallsPanel
          calls={openCalls}
          views={views}
          onOpen={onOpen}
          organizationId={venue.organizationId}
          venueId={venue.id}
          canManage={access.status === 'ready' && access.can(CALLS_MANAGE)}
        />
      ) : null}
      <KioskTopUps organizationId={venue.organizationId} venueId={venue.id} enabled={canTopUp} />
      {needs.data === undefined ? null : <AttentionSection needs={needs.data} views={views} onOpen={onOpen} />}
      <BootProblemsSection cards={bootCards} access={access} venueSlug={venue.slug} scope={{ organizationId: venue.organizationId, venueId: venue.id }} bootFull={bootFull} onOpen={onOpen} />
      <StockNeedsSection alerts={stockNeeds.alerts} venueSlug={venue.slug} />
      <OfflinePlaySection count={settleCount} venueSlug={venue.slug} />
      <ShiftVarianceSection
        shifts={varianceShifts}
        onOpen={(shiftId) => void navigate({ to: '/v/$venueSlug/shifts', params: { venueSlug: venue.slug }, search: { shift: shiftId } })}
      />

      <PanelSection title={t('floor.needsYou.overdue')} items={overdue} onOpen={onOpen} tone="danger" />
      <PanelSection title={t('floor.needsYou.ending')} items={ending} onOpen={onOpen} tone="warn" />
      <FreeNowByClass items={free} classById={classById} onOpen={onOpen} />
      <OfflineSection items={offline} onOpen={onOpen} organizationId={venue.organizationId} venueId={venue.id} canPower={canPower} />
      <PanelSection title={t('floor.needsYou.outOfOrder')} items={disabled} onOpen={onOpen} tone="neutral" />
      <PanelSection title={t('floor.needsYou.maintenance')} items={maintenance} onOpen={onOpen} tone="neutral" />
    </aside>
  );
}

// "Played offline" (spec/13 §3.2 item 2, §3.6): sessions the PCs ran while they could not reach Clavian that staff must
// still settle — paid at the desk, or comp. One line with how many, opening Offline play where each is settled.
function OfflinePlaySection({ count, venueSlug }: { readonly count: number; readonly venueSlug: string }): ReactNode {
  const { t } = useTranslation('console');
  if (count === 0) return null;
  return (
    <section className="console-floor__panel-section">
      <h3 className="console-floor__panel-subtitle">
        <span>{t('floor.needsYou.offlinePlay')}</span>
        <Badge tone="warn">{count}</Badge>
      </h3>
      <ul className="console-floor__panel-list">
        <li>
          <Link to="/v/$venueSlug/sessions/offline" params={{ venueSlug }} className="console-floor__panel-item">
            <span className="console-floor__panel-name">{t('floor.needsYou.offlinePlayItem', { count })}</span>
            <span className="console-floor__panel-go" aria-hidden="true">
              <Icon name="chevronRight" />
            </span>
          </Link>
        </li>
      </ul>
    </section>
  );
}

// "Shift variance" (F-OPS-07, spec/13 §10): a shift closed with a variance over `shifts.varianceAlertAmount`. Each
// opens the shift — expected vs counted per tender, the cashier's note, who closed it. Gone once the day is closed.
function ShiftVarianceSection({ shifts, onOpen }: { readonly shifts: readonly Shift[]; readonly onOpen: (shiftId: string) => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  if (shifts.length === 0) return null;
  return (
    <section className="console-floor__panel-section">
      <h3 className="console-floor__panel-subtitle">
        <span>{t('floor.needsYou.shiftVariance')}</span>
        <Badge tone="danger">{shifts.length}</Badge>
      </h3>
      <ul className="console-floor__panel-list">
        {shifts.map((shift) => {
          const variance = shift.cashVariance ?? { amount: 0, currency: shift.openingFloat.currency };
          const word = t(`shifts.varianceWord.${variance.amount > 0 ? 'over' : 'short'}`);
          return (
            <li key={shift.id}>
              <button type="button" className="console-floor__panel-item" onClick={() => onOpen(shift.id)}>
                <span className="console-floor__panel-name">
                  {t('floor.needsYou.shiftVarianceItem', {
                    counter: shift.counterName ?? t('shifts.noCounter'),
                    amount: format.money({ amount: Math.abs(variance.amount), currency: variance.currency }),
                    word,
                  })}
                </span>
                <span className="console-floor__panel-state console-num">{shift.closedAt === null ? '' : format.dateTime(shift.closedAt, { time: 'short' })}</span>
                <span className="console-floor__panel-go" aria-hidden="true">
                  <Icon name="chevronRight" />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// "Free now", by class (spec/13 §3.2 item 6): each class with how many stations are free, click to open
// the first free one. Grouped by class so staff can seat a walk-in by the class they asked for.
function FreeNowByClass({
  items,
  classById,
  onOpen,
}: {
  readonly items: readonly FloorStationView[];
  readonly classById: ReadonlyMap<string, StationClass>;
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const groups = useMemo(() => {
    const byClass = new Map<string, FloorStationView[]>();
    for (const item of items) {
      const list = byClass.get(item.station.classId) ?? [];
      list.push(item);
      byClass.set(item.station.classId, list);
    }
    return [...byClass.entries()]
      .map(([classId, list]) => {
        const klass = classById.get(classId);
        const sorted = [...list].sort((a, b) => a.station.number - b.station.number);
        return { classId, name: klass?.name ?? classId, colorToken: klass?.colorToken, first: sorted[0], count: sorted.length };
      })
      .sort((a, b) => (a.first?.station.number ?? 0) - (b.first?.station.number ?? 0));
  }, [items, classById]);
  if (groups.length === 0) return null;

  return (
    <section className="console-floor__panel-section">
      <h3 className="console-floor__panel-subtitle">
        <span>{t('floor.needsYou.free')}</span>
        <Badge tone="neutral">{items.length}</Badge>
      </h3>
      <ul className="console-floor__panel-list">
        {groups.map((group) => (
          <li key={group.classId}>
            <button type="button" className="console-floor__panel-item" disabled={group.first === undefined} onClick={() => group.first !== undefined && onOpen(group.first.station.stationId)}>
              <span className="console-floor__class-dot" style={{ backgroundColor: group.colorToken === undefined ? undefined : `var(--clavian-${group.colorToken})` }} aria-hidden="true" />
              <span className="console-floor__panel-name">{group.name}</span>
              <span className="console-floor__panel-state console-num">{t('floor.needsYou.freeCount', { count: group.count })}</span>
              <span className="console-floor__panel-go" aria-hidden="true">
                <Icon name="chevronRight" />
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// "Offline", with "Wake all offline" (spec/13 §3.2 item 7): the list, and a bulk wake gated by
// `stations:power` behind a confirmation, since it acts on every offline station at once.
function OfflineSection({
  items,
  onOpen,
  organizationId,
  venueId,
  canPower,
}: {
  readonly items: readonly FloorStationView[];
  readonly onOpen: (stationId: string) => void;
  readonly organizationId: string;
  readonly venueId: string;
  readonly canPower: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const power = useStationPower(organizationId, venueId);
  const [confirming, setConfirming] = useState(false);
  if (items.length === 0) return null;

  const wakeAll = () => {
    setConfirming(false);
    for (const item of items) {
      power.mutate(
        { stationId: item.station.stationId, body: { action: 'wake' } },
        { onError: () => undefined },
      );
    }
    show({ tone: 'ok', title: t('floor.needsYou.wakingAll', { count: items.length }) });
  };

  return (
    <section className="console-floor__panel-section">
      <h3 className="console-floor__panel-subtitle">
        <span>{t('floor.needsYou.offline')}</span>
        <Badge tone="neutral">{items.length}</Badge>
      </h3>
      <ul className="console-floor__panel-list">
        {items.map((item) => (
          <li key={item.station.stationId}>
            <button type="button" className="console-floor__panel-item" onClick={() => onOpen(item.station.stationId)}>
              <span className="console-keycap console-floor__panel-number">{item.station.number}</span>
              <span className="console-floor__panel-name">{item.station.name ?? t('floor.tile.numbered', { number: item.station.number })}</span>
              <span className="console-floor__panel-state console-num">{pillOf(item, t).words}</span>
              <span className="console-floor__panel-go" aria-hidden="true">
                <Icon name="chevronRight" />
              </span>
            </button>
          </li>
        ))}
      </ul>
      {canPower ? (
        confirming ? (
          <Notice
            tone="warn"
            role="alert"
            title={t('floor.needsYou.wakeAllConfirm', { count: items.length })}
            action={
              <>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  {t('floor.confirm.cancel')}
                </Button>
                <Button variant="secondary" size="sm" busy={power.isPending} onClick={wakeAll}>
                  {t('floor.needsYou.wakeAll')}
                </Button>
              </>
            }
          />
        ) : (
          <Button variant="secondary" size="sm" icon={<Icon name="power" />} onClick={() => setConfirming(true)}>
            {t('floor.needsYou.wakeAll')}
          </Button>
        )
      ) : null}
    </section>
  );
}

function PanelSection({
  title,
  items,
  onOpen,
  tone,
}: {
  readonly title: string;
  readonly items: readonly FloorStationView[];
  readonly onOpen: (stationId: string) => void;
  readonly tone: 'neutral' | 'warn' | 'danger';
}): ReactNode {
  const { t } = useTranslation('console');
  if (items.length === 0) return null;
  return (
    <section className="console-floor__panel-section">
      <h3 className="console-floor__panel-subtitle">
        <span>{title}</span>
        <Badge tone={tone}>{items.length}</Badge>
      </h3>
      <ul className="console-floor__panel-list">
        {items.map((item) => {
          const pill = pillOf(item, t);
          return (
            <li key={item.station.stationId}>
              <button type="button" className={cx('console-floor__panel-item', tone === 'danger' && 'console-floor__panel-item--danger')} onClick={() => onOpen(item.station.stationId)}>
                <span className="console-keycap console-floor__panel-number">{item.station.number}</span>
                <span className="console-floor__panel-name">{item.station.name ?? t('floor.tile.numbered', { number: item.station.number })}</span>
                <span className="console-floor__panel-state console-num">{pill.words}</span>
                <span className="console-floor__panel-go" aria-hidden="true">
                  <Icon name="chevronRight" />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// "Needs attention" (F-STN-15, spec/13 §3.2 item 2): the station health alerts that are open — critical first — and
// the commands that failed or expired, each opening its station's sheet. An alert leaves this list, and its tile's
// badge, when the station reports the condition cleared; nobody dismisses it by hand.
// ---------------------------------------------------------------------------------------------

/** The translation key for a command type in "Needs attention". */
function commandKey(type: StationCommandIssue['type']): string {
  switch (type) {
    case 'station.power':
      return 'power';
    case 'station.mode.set':
      return 'modeSet';
    case 'session.lock':
      return 'lock';
    case 'session.unlock':
      return 'unlock';
    case 'session.end':
      return 'end';
    case 'session.message':
      return 'message';
    case 'station.policy.update':
      return 'policy';
    case 'station.library.update':
      return 'library';
    case 'station.call.updated':
      return 'callBanner';
    default:
      return 'other';
  }
}

function AttentionSection({
  needs,
  views,
  onOpen,
}: {
  readonly needs: NeedsAttention;
  readonly views: readonly FloorStationView[];
  readonly onOpen: (stationId: string) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const now = useNow();
  const count = needs.alerts.length + needs.commands.length;
  if (count === 0) return null;
  const byId = new Map(views.map((item) => [item.station.stationId, item.station]));
  const nameOf = (stationId: string): { readonly number: string; readonly name: string } => {
    const station = byId.get(stationId);
    if (station === undefined) return { number: '', name: t('floor.drawer.station') };
    return { number: String(station.number), name: station.name ?? t('floor.tile.numbered', { number: station.number }) };
  };
  const critical = needs.alerts.some((alert) => alert.severity === 'critical');

  return (
    <section className="console-floor__panel-section">
      <h3 className="console-floor__panel-subtitle">
        <span>{t('floor.needsYou.attention')}</span>
        <Badge tone={critical ? 'danger' : 'warn'}>{count}</Badge>
      </h3>
      <ul className="console-floor__panel-list">
        {needs.alerts.map((alert) => {
          const station = nameOf(alert.stationId);
          return (
            <li key={alert.id}>
              <button
                type="button"
                className={cx('console-floor__panel-item', alert.severity === 'critical' && 'console-floor__panel-item--danger')}
                onClick={() => onOpen(alert.stationId)}
              >
                <span className="console-keycap console-floor__panel-number">{station.number}</span>
                <span className="console-floor__panel-name">
                  {station.name}
                  <span className="console-floor__attention-detail">{alertWords(alert.kind, t)}</span>
                </span>
                <span className="console-floor__panel-state console-num">{formatAgo(alert.openedAt, t, now)}</span>
                <span className="console-floor__panel-go" aria-hidden="true">
                  <Icon name="chevronRight" />
                </span>
              </button>
            </li>
          );
        })}
        {needs.commands.map((command) => {
          const station = nameOf(command.stationId);
          const what = t(`floor.commandIssue.command.${commandKey(command.type)}`);
          // The station's reason in plain words, by its error code; its own error message is technical and not shown (2026-10-07).
          const reason = command.status === 'failed' && command.errorCode !== null ? t(`floor.commandIssue.reason.${command.errorCode}`, { defaultValue: '' }) : '';
          const words =
            command.status !== 'failed'
              ? t('floor.commandIssue.expired', { command: what })
              : reason === ''
                ? t('floor.commandIssue.failed', { command: what })
                : t('floor.commandIssue.failedWith', { command: what, reason });
          return (
            <li key={command.id}>
              <button type="button" className="console-floor__panel-item" onClick={() => onOpen(command.stationId)}>
                <span className="console-keycap console-floor__panel-number">{station.number}</span>
                <span className="console-floor__panel-name">
                  {station.name}
                  <span className="console-floor__attention-detail">{words}</span>
                </span>
                <span className="console-floor__panel-state console-num">{formatAgo(command.completedAt, t, now)}</span>
                <span className="console-floor__panel-go" aria-hidden="true">
                  <Icon name="chevronRight" />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------
// Calls for help (F-SES-25, spec/13 §3.2): the first thing in "What needs you", oldest first, each
// naming its station — which opens it — and actionable in place with On my way / Done.
// ---------------------------------------------------------------------------------------------

const OPEN_CALL_STATES: ReadonlySet<StationCall['state']> = new Set(['open', 'acknowledged']);

function CallsPanel({
  calls,
  views,
  onOpen,
  organizationId,
  venueId,
  canManage,
}: {
  readonly calls: readonly StationCall[];
  /** The floor's stations: a call names the one it came from by its number and name. */
  readonly views: readonly FloorStationView[];
  readonly onOpen: (stationId: string) => void;
  readonly organizationId: string;
  readonly venueId: string;
  readonly canManage: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const acknowledge = useAcknowledgeCall(organizationId, venueId);
  const resolve = useResolveCall(organizationId, venueId);
  const now = useNow();
  const byId = new Map(views.map((item) => [item.station.stationId, item]));

  const open = [...calls].sort((a, b) => a.raisedAt.localeCompare(b.raisedAt));
  if (open.length === 0) return null;

  return (
    <section className="console-floor__panel-section">
      <h3 className="console-floor__panel-subtitle">
        <span>{t('floor.calls.title')}</span>
        <Badge tone="danger">{open.length}</Badge>
      </h3>
      <ul className="console-floor__calls">
        {open.map((call) => {
          const view = byId.get(call.stationId);
          const station = view === undefined ? null : (view.station.name ?? t('floor.tile.numbered', { number: view.station.number }));
          return (
          <li key={call.id} className="console-floor__call">
            <div className="console-floor__call-body">
              {/* Which station called (spec/13 §3.2): its number and name, opening its modal — and who is on it. */}
              {view === undefined || station === null ? (
                <span className="console-floor__call-category">{t('floor.calls.unknownStation')}</span>
              ) : (
                <button type="button" className="console-floor__call-station" onClick={() => onOpen(call.stationId)}>
                  <span className="console-figure" aria-hidden="true">
                    {view.station.number}
                  </span>
                  <span>{view.playerName === null ? station : t('floor.calls.stationWithPlayer', { station, player: view.playerName })}</span>
                </button>
              )}
              <span className="console-floor__call-category">
                <Icon name="hand" />
                {t(`floor.calls.category.${call.category}`)}
              </span>
              {call.note !== null ? <p className="console-floor__call-note">{call.note}</p> : null}
              <span className="console-subtle">
                {formatAgo(call.raisedAt, t, now)}
                {call.state === 'acknowledged' && call.acknowledgedByName !== null
                  ? ` · ${t('floor.calls.onWay', { name: call.acknowledgedByName })}`
                  : null}
              </span>
            </div>
            {canManage ? (
              <div className="console-floor__call-actions">
                {call.state === 'open' ? (
                  <Button variant="primary" size="sm" busy={acknowledge.isPending} onClick={() => acknowledge.mutate({ callId: call.id })}>
                    {t('floor.calls.onMyWay')}
                  </Button>
                ) : null}
                <Button variant="secondary" size="sm" busy={resolve.isPending} onClick={() => resolve.mutate({ callId: call.id })}>
                  {t('floor.calls.done')}
                </Button>
              </div>
            ) : null}
          </li>
          );
        })}
      </ul>
    </section>
  );
}

